import { RiskConfig } from '@Types/risk.types';
import { noConditionData } from '../RiskDetails.component';
import { AssetIdCell } from './RenderCell/AssetIdCell';
import { ConditionCell } from './RenderCell/ConditionCell';
import { MenuCell } from './RenderCell/MenuCell';
import { StatusCell } from './RenderCell/StatusCell';
import { TrendCell } from './RenderCell/TrendCell';
import {
	GridColDef,
	GridFilterOperator,
	getGridSingleSelectOperators,
	useGlobalization,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import React from 'react';
import useGetWeightedScores from './useGetWeightedScores';

/* eslint-disable react/display-name */

export function useRiskDetailsColumns(riskConfig: RiskConfig): GridColDef[] {
	const { t } = useGlobalization();
	const weightedScores = useGetWeightedScores();
	const isIndividualScoresEnabled = useIsFeatureEnabled(
		'info-360-risk-datagrid-individual-scores',
	);

	const columns: GridColDef[] = [
		{
			field: 'ASSET_ID',
			headerName: t('Asset ID'),
			align: 'left',
			renderCell: params => (
				<AssetIdCell assetId={params.value as string} />
			),
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
			hideable: false,
		},
		{
			field: 'RISK_CHANGE',
			align: 'left',
			headerName: t('Risk Trend'),
			renderCell: params => <TrendCell trend={params.value as string} />,
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
		{
			field: 'CURRENT_RISK',
			align: 'left',
			headerName: t('Risk Score'),
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
		{
			field: 'CURRENT_LOF',
			align: 'left',
			headerName: t('Total LoF'),
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
		{
			field: 'CURRENT_COF',
			align: 'left',
			headerName: t('Total CoF'),
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
	];

	if (!noConditionData(riskConfig.riskSetting.assetType)) {
		columns.push({
			field: 'CONDITION_DATA',
			align: 'left',
			headerName: t('Condition Data'),
			renderCell: params => (
				<ConditionCell condition={params.value as string} />
			),
			minWidth: 150,
			flex: 1,
			sortable: false,
			type: 'singleSelect',
			valueOptions: [
				{
					label: t('No'),
					value: '0',
				},
				{
					label: t('Yes'),
					value: '1',
				},
			],
			filterable: !noConditionData(riskConfig.riskSetting.assetType),
			filterOperators: getGridSingleSelectOperators().filter(
				(operator: GridFilterOperator) => {
					return operator.value == 'is';
				},
			),
		});
	}

	columns.push(
		{
			field: 'GRADE',
			align: 'left',
			headerName: t('Grade'),
			renderCell: params => (
				<StatusCell status={params.value as string} />
			),
			minWidth: 150,
			width: 150,
			sortable: true,
			filterable: true,
			type: 'singleSelect',
			valueOptions: [
				{
					label: t('Negligible'),
					value: '0',
				},
				{
					label: t('Low'),
					value: '1',
				},
				{
					label: t('Medium'),
					value: '2',
				},
				{
					label: t('High'),
					value: '3',
				},
				{
					label: t('Extreme'),
					value: '4',
				},
			],
			filterOperators: getGridSingleSelectOperators().filter(
				(operator: GridFilterOperator) => {
					return (
						operator.value == 'is' || operator.value == 'isAnyOf'
					);
				},
			),
		},
		{
			field: 'Menu',
			headerName: '',
			sortable: false,
			filterable: false,
			hideable: false,
			disableColumnMenu: true,
			width: 68,
			maxWidth: 68,
			minWidth: 68,
			renderCell: params => (
				<MenuCell
					assetId={params.value as string}
					assetType={riskConfig.riskSetting.assetType}
				/>
			),
		},
	);

	if (isIndividualScoresEnabled) {
		if (weightedScores?.lofScores?.length) {
			columns.push(...weightedScores.lofScores);
		}
		if (weightedScores?.cofScores?.length) {
			columns.push(...weightedScores.cofScores);
		}
	}

	return columns;
}
